import * as Icons from "@ant-design/icons";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  InboxOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  UploadOutlined
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Progress,
  Select,
  Skeleton,
  Space,
  Spin,
  Steps,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../../../../store/context/tenant-context";
import { postAuthenticatedRequest } from "../../../../../utils/api/requests/request";
import { formatCurrencyWithoutCode } from "../../../../../utils/helper/format";
import CustomSearchDropdown from "../../../../General/SelectionDropdown";
import * as XLSX from 'xlsx';

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Step } = Steps;
const { Dragger } = Upload;
const { TextArea } = Input;

// Helper functions outside component to prevent recreation
const getIconComponent = (iconName) => {
  if (!iconName) return null;
  if (typeof iconName === "object") return iconName;

  const iconMap = {
    ArrowLeftOutlined: ArrowLeftOutlined,
    CheckOutlined: CheckOutlined,
    CloseOutlined: CloseOutlined,
    DeleteOutlined: DeleteOutlined,
    EditOutlined: EditOutlined,
    EyeOutlined: EyeOutlined,
    FileExcelOutlined: FileExcelOutlined,
    InboxOutlined: InboxOutlined,
    MoreOutlined: MoreOutlined,
    PaperClipOutlined: PaperClipOutlined,
    PlusOutlined: PlusOutlined,
    ReloadOutlined: ReloadOutlined,
    SaveOutlined: SaveOutlined,
    UploadOutlined: UploadOutlined,
  };

  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent /> : null;
};

const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

const getNestedValue = (obj, path) => {
  if (!path || !obj) return null;
  return path
    .split(".")
    .reduce((o, key) => (o && o[key] !== undefined ? o[key] : null), obj);
};

const hexToRgba = (hex, a = 0.06) => {
  if (!hex) return `rgba(11,95,255,${a})`;
  hex = hex.replace("#", "").trim();
  if (hex.startsWith("rgb")) {
    return hex.replace("rgb(", "rgba(").replace(")", `, ${a})`);
  }
  if (hex.length === 3) {
    hex = hex.split("").map(c => c + c).join("");
  }
  const int = parseInt(hex, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const parseExcelFile = async (file, excelConfig) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const headers = jsonData[0];
        const requiredHeaders = excelConfig?.requiredHeaders || ["key", "source", "glAccount", "branch", "accountNumber", "customerName", "type", "amount", "reference", "description"];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

        if (missingHeaders.length > 0) {
          reject(new Error(`Missing required headers: ${missingHeaders.join(', ')}`));
          return;
        }

        const entries = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.every(cell => cell === null || cell === undefined || cell === '')) {
            continue;
          }

          const entry = {};
          headers.forEach((header, index) => {
            entry[header] = row[index];
          });

          const transformedEntry = {
            key: entry.key,
            source: entry.source,
            glAccount: entry.glAccount,
            branch: entry.branch,
            accountName: entry.customerName || entry.accountNumber || "N/A",
            branchName: entry.branch || "N/A",
            reference: entry.reference || `JE-${Date.now()}`,
            description: entry.description || "No description",
            debitAmount: entry.type === 'debit' ? parseFloat(entry.amount) || 0 : 0,
            creditAmount: entry.type === 'credit' ? parseFloat(entry.amount) || 0 : 0,
            customerName: entry.customerName,
            accountNumber: entry.accountNumber,
            originalData: entry
          };

          entries.push(transformedEntry);
        }

        resolve({
          fileName: file.name,
          entries,
          headers,
          totalEntries: entries.length,
          totalDebit: entries.reduce((sum, e) => sum + (e.debitAmount || 0), 0),
          totalCredit: entries.reduce((sum, e) => sum + (e.creditAmount || 0), 0),
          isValid: true
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

const DynamicDrawer = ({
  visible,
  onClose,
  formFieldsColumn,
  configCmd,
  configParams = {},
  width = "40%",
  placement = "right",
  closable = true,
  destroyOnClose = true,
  initialMode = null,
  recordId = null,
  record = null,
  onModeChange = null,
  onRefresh = () => { },
  extraParams = {},
  onSaveMethod = null,
}) => {
  const { t } = useTranslation();
  const tenant = useTenant();

  // Refs to track state
  const recordIdRef = useRef(null);
  const recordRef = useRef(null);
  const configRef = useRef(null);
  const initializedRef = useRef(false);
  const mountedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [config, setConfig] = useState(null);

  const [data, setData] = useState({});
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("main");
  const [pageMode, setPageMode] = useState("LIST");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [selectOptions, setSelectOptions] = useState({});

  // Field validation states
  const [fieldValidations, setFieldValidations] = useState({});
  const [validationLoading, setValidationLoading] = useState({});

  // Current record ID
  const [currentRecordId, setCurrentRecordId] = useState(null);

  // Review screen states - FIXED: Should start at 0 (Form step)
  const [showReview, setShowReview] = useState(false);
  const [reviewData, setReviewData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);

  // Upload states
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  // File lists for all upload fields
  const [fileLists, setFileLists] = useState({});
  const [fileData, setFileData] = useState({});

  // Helper function to get default value based on field type
  const getDefaultValueForFieldType = useCallback((field) => {
    if (field.defaultValue !== undefined) {
      return field.defaultValue;
    }

    const fieldType = field.fieldType || field.type;

    switch (fieldType) {
      case "input":
      case "password":
      case "textarea":
        return "";
      case "select":
      case "customSelect":
        return field.multiple ? [] : undefined;
      case "switch":
      case "checkbox":
        return false;
      case "date":
      case "dateRange":
        return null;
      case "number":
        return 0;
      case "upload":
        return field.multiple ? [] : null;
      default:
        return "";
    }
  }, []);

  // Generate initial form values from form fields
  const generateInitialFormValues = useCallback(
    (formFields) => {
      if (!formFields || !Array.isArray(formFields)) return {};

      const initialValues = {};
      formFields.forEach((field) => {
        if (field.name) {
          initialValues[field.name] = getDefaultValueForFieldType(field);
        }
      });

      return initialValues;
    },
    [getDefaultValueForFieldType]
  );

  // Safe evaluate function - simplified dependencies
  const safeEvaluateFunction = useCallback(
    (funcString, context = {}) => {
      if (typeof funcString !== "string") return funcString;

      try {
        // Handle template expressions like ${record.fieldName}
        if (funcString.includes("${record.") || funcString.includes("${record[")) {
          let result = funcString;
          const record = context.record || selectedRecord;

          if (record) {
            result = funcString.replace(
              /\$\{record\.([^}]+)\}/g,
              (match, fieldPath) => {
                const value = fieldPath
                  .split(".")
                  .reduce(
                    (obj, key) => obj && obj[key] !== undefined ? obj[key] : null,
                    record
                  );
                return value !== null && value !== undefined ? String(value) : "";
              }
            );
          }
          return result;
        }

        if (funcString.includes("=>") || funcString.startsWith("(")) {
          const func = new Function(
            "data",
            "record",
            "index",
            "formValues",
            "mode",
            "selectOptions",
            "tenant",
            `return ${funcString}`
          );
          return func(
            context.data || data,
            context.record || selectedRecord,
            context.index,
            context.formValues || formValues,
            context.mode || pageMode,
            selectOptions,
            tenant
          );
        }
        return funcString;
      } catch (error) {
        console.error("Error evaluating function:", error, funcString);
        return funcString;
      }
    },
    [data, formValues, pageMode, selectOptions, selectedRecord, tenant]
  );

  // Check if review screen is enabled
  const isReviewEnabled = useMemo(() => {
    return config?.enableReview === "true";
  }, [config?.enableReview]);

  // Steps configuration - FIXED: Only show Complete step after saving
  const steps = useMemo(() => {
    const baseSteps = [
      {
        title: "Form",
        key: "form",
      },
    ];

    if (isReviewEnabled) {
      baseSteps.push({
        title: "Review",
        key: "review",
      });
    }

    return baseSteps;
  }, [isReviewEnabled]);

  // Handle step changes
  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, steps.length]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // Load select options from commands
  const loadSelectOptions = useCallback(async (field) => {
    if (!field.optionsCmd) return;

    try {
      const rawParams = field.optionsParams || {};
      const evaluatedParams = Object.fromEntries(
        Object.entries(rawParams).map(([key, value]) => [
          key,
          typeof value === "string"
            ? safeEvaluateFunction(value, {
              data,
              record: selectedRecord,
              formValues,
              mode: pageMode,
              tenant,
            })
            : value,
        ])
      );

      const response = await postAuthenticatedRequest(
        field.optionsCmd,
        evaluatedParams
      );
      const result = response?.outData;

      if (result?.isSuccessful) {
        let optionsData = result.data;

        if (field.optionsPath) {
          const pathParts = field.optionsPath.split(".");
          optionsData = pathParts.reduce((obj, key) => obj && obj[key], result.data) || [];
        }

        const options = Array.isArray(optionsData)
          ? optionsData.map((item) => ({
            value: item[field.valueField || "value"],
            label: item[field.labelField || "label"],
            detail: item[field.detail || "detail"],
          }))
          : [];

        setSelectOptions((prev) => ({
          ...prev,
          [field.name]: options,
        }));
      } else {
        console.error("Failed to load options:", result?.message);
      }
    } catch (error) {
      console.error("Error loading select options:", error);
    }
  }, [data, formValues, pageMode, selectedRecord, safeEvaluateFunction, tenant]);

  // Load options for all fields that have optionsCmd
  const loadAllSelectOptions = useCallback(
    async (formFields) => {
      if (!formFields) return;

      const optionFields = formFields.filter((field) => field.optionsCmd);
      for (const field of optionFields) {
        await loadSelectOptions(field);
      }
    },
    [loadSelectOptions]
  );

  // Field validation function
  const validateField = useCallback(async (fieldName, value) => {
    const field = config?.formFields?.find(f => f.name === fieldName);

    if (!field?.validationButton) return;

    try {
      setValidationLoading(prev => ({ ...prev, [fieldName]: true }));

      const { cmd, params = {}, targetField, responsePath, targetFieldBeName, transform } = field.validationButton;

      const dynamicParams = {};
      Object.entries(params).forEach(([key, paramValue]) => {
        if (typeof paramValue === 'string' && paramValue.includes('${')) {
          dynamicParams[key] = safeEvaluateFunction(paramValue, {
            formValues,
            record: selectedRecord,
            tenant,
          });
        } else {
          dynamicParams[key] = paramValue;
        }
      });

      const backendParamName = targetFieldBeName || fieldName;

      const requestParams = {
        [backendParamName]: value, // Use backendParamName instead of fieldName
        ...dynamicParams,
        ...extraParams
      };


      const response = await postAuthenticatedRequest(cmd, requestParams);
      const result = response?.outData;

      if (result?.isSuccessful) {


        let targetValue = null;
        if (responsePath) {
          // Start from the entire response object, not just result
          targetValue = responsePath.split('.').reduce((obj, key) => {
            // Handle array indices like "data[0].name" or "data.0.name"
            const arrayMatch = key.match(/^(\d+)$/);
            if (arrayMatch && Array.isArray(obj)) {
              return obj[parseInt(arrayMatch[1])];
            }
            return obj && obj[key] !== undefined ? obj[key] : undefined;
          }, response); // Start from the full response, not just result
        } else {
          // If no responsePath specified, use outData.data
          targetValue = result?.data;
        }

        if (transform && typeof transform === 'string') {
          try {
            const transformFunc = new Function('value', 'data', 'record', `return ${transform}`);
            targetValue = transformFunc(targetValue, result, selectedRecord);
          } catch (error) {
            console.error('Transform function error:', error);
          }
        }

        if (targetField && targetValue !== undefined) {
          if (Array.isArray(targetField)) {
            const updates = {};
            targetField.forEach((field, index) => {
              if (Array.isArray(targetValue) && targetValue[index] !== undefined) {
                updates[field] = targetValue[index];
              } else if (typeof targetValue === 'object' && targetValue[field] !== undefined) {
                updates[field] = targetValue[field];
              }
            });

            form.setFieldsValue(updates);
            setFormValues(prev => ({ ...prev, ...updates }));
          } else {
            form.setFieldsValue({ [targetField]: targetValue });
            setFormValues(prev => ({ ...prev, [targetField]: targetValue }));
          }

          form.setFieldsValue({ [fieldName]: value });
        }

        setFieldValidations(prev => ({
          ...prev,
          [fieldName]: value
        }));
        console.log("targetValue ... " + targetValue)
        message.success(`${field.validationButton.successMessage || 'Validation successful'} ${targetValue}`);
      } else {
        setFieldValidations(prev => ({
          ...prev,
          [fieldName]: {
            isValid: false,
            message: result?.message || 'Validation failed',
            timestamp: Date.now()
          }
        }));

        message.error(result?.message || 'Validation failed');
      }
    } catch (error) {
      console.error(`Validation error for ${fieldName}:`, error);
      setFieldValidations(prev => ({
        ...prev,
        [fieldName]: {
          isValid: false,
          message: 'Validation error',
          timestamp: Date.now()
        }
      }));
      message.error('Validation error');
    } finally {
      setValidationLoading(prev => ({ ...prev, [fieldName]: false }));
    }
  }, [config, form, formValues, selectedRecord, safeEvaluateFunction, tenant, extraParams]);

  const handleFormValuesChange = useCallback(
    (changedValues, allValues) => {
      setFormValues(allValues);

      Object.keys(changedValues).forEach(fieldName => {
        if (fieldValidations[fieldName]) {
          setFieldValidations(prev => ({ ...prev, [fieldName]: null }));
        }
      });
    },
    [fieldValidations]
  );

  const isFieldDisabled = useCallback(
    (field) => {
      if (field.disabled !== undefined) {
        if (typeof field.disabled === "boolean") {
          return field.disabled;
        }
        if (typeof field.disabled === "string") {
          return safeEvaluateFunction(field.disabled, {
            data,
            formValues,
            mode: pageMode,
            record: selectedRecord,
            tenant,
          });
        }
      }

      if (field.disabledInEdit && pageMode === "EDIT") {
        return true;
      }

      if (field.disabledInNew && pageMode === "NEW") {
        return true;
      }

      if (field.disabledWhen) {
        const {
          field: dependentField,
          value,
          operator = "===",
        } = field.disabledWhen;
        const dependentValue = formValues[dependentField];

        switch (operator) {
          case "===":
            return dependentValue === value;
          case "!==":
            return dependentValue !== value;
          case "==":
            return dependentValue == value;
          case "!=":
            return dependentValue != value;
          case "truthy":
            return !!dependentValue;
          case "falsy":
            return !dependentValue;
          default:
            return false;
        }
      }

      return false;
    },
    [formValues, pageMode, selectedRecord, data, safeEvaluateFunction, tenant]
  );

  const getDynamicFieldType = useCallback(
    (field) => {
      if (!field.dynamicFieldType) return field.fieldType;

      const { field: watchedField, conditions } = field.dynamicFieldType;
      const watchedValue = formValues[watchedField];

      const matchedCondition = conditions.find((condition) => {
        const { value, operator = "===" } = condition;
        switch (operator) {
          case "===":
            return `${watchedValue}` === value;
          case "!==":
            return `${watchedValue}` !== value;
          case "==":
            return `${watchedValue}` == value;
          case "!=":
            return `${watchedValue}` != value;
          case "truthy":
            return !!`${watchedValue}`;
          case "falsy":
            return !`${watchedValue}`;
          default:
            return false;
        }
      });

      return matchedCondition ? matchedCondition.fieldType : field.fieldType;
    },
    [formValues]
  );

  // Handle file preview
  const handlePreview = useCallback(async (file) => {
    if (!file.url && !file.preview) {
      if (file.originFileObj) {
        file.preview = await getBase64(file.originFileObj);
      }
    }

    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url?.substring(file.url.lastIndexOf('/') + 1) || 'Preview');
  }, []);

  // Enhanced upload field component - FIXED: Excel upload issue
  const renderUploadField = useCallback((item) => {
    const {
      name,
      label,
      multiple = false,
      maxCount = 1,
      maxSize = 10,
      accept = "*/*",
      listType = "text",
      showUploadList = true,
      disabled = false,
      draggable = false,
      excelConfig,
    } = item;

    const isDisabled = disabled || isFieldDisabled(item);
    const fileList = fileLists[name] || [];

    // FIXED: Don't check maxCount in beforeUpload if maxCount is undefined or null
    const actualMaxCount = maxCount === undefined || maxCount === null ? undefined : maxCount;

    const customUpload = async (options) => {
      const { file, onSuccess, onError } = options;

      try {
        // Validate file size
        const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
        if (!isLtMaxSize) {
          message.error(`File must be smaller than ${maxSize}MB!`);
          onError(new Error(`File size exceeds ${maxSize}MB limit`));
          return false;
        }

        // Validate file type
        if (accept !== "*/*") {
          const acceptTypes = accept.split(",").map(type => type.trim());
          const fileType = file.type || "";
          const fileName = file.name || "";
          const isValidType = acceptTypes.some(type => {
            if (type.startsWith(".")) {
              return fileName.toLowerCase().endsWith(type.toLowerCase());
            }
            if (type.includes("/*")) {
              const baseType = type.replace("/*", "/");
              return fileType.startsWith(baseType);
            }
            return fileType === type;
          });

          if (!isValidType) {
            message.error(`File type not allowed. Allowed types: ${accept}`);
            onError(new Error(`Invalid file type`));
            return false;
          }
        }

        // Parse Excel file if needed
        let excelData = null;
        const isExcel = file.name.match(/\.(xls|xlsx)$/i);

        if (isExcel && excelConfig) {
          try {
            excelData = await parseExcelFile(file, excelConfig);
            file.excelData = excelData;
            message.success(`Excel file parsed successfully: ${excelData.entries.length} entries found`);
          } catch (excelError) {
            console.error('Error parsing Excel file:', excelError);
            message.error(`Excel parsing error: ${excelError.message}`);
            onError(excelError);
            return false;
          }
        }

        // Create uploaded file object
        const uploadedFile = {
          uid: file.uid,
          name: file.name,
          status: 'done',
          size: file.size,
          type: file.type,
          originFileObj: file,
          excelData: excelData,
          lastModified: file.lastModified,
        };

        // Store actual file data
        setFileData(prev => ({
          ...prev,
          [name]: multiple
            ? [...(prev[name] || []), file]
            : [file]
        }));

        // Update form values
        const doneFiles = [...fileList, uploadedFile]
          .filter(f => f.status === 'done')
          .map(f => ({
            name: f.name,
            size: f.size,
            type: f.type,
            lastModified: f.lastModified,
            excelData: f.excelData,
          }));

        const formValue = multiple ? doneFiles : doneFiles[0] || null;

        form.setFieldsValue({ [name]: formValue });
        setFormValues(prev => ({ ...prev, [name]: formValue }));

        onSuccess(uploadedFile, file);
        return false;

      } catch (error) {
        console.error('Upload error:', error);
        onError(error);
        message.error('Upload failed');
        return false;
      }
    };

    const beforeUpload = (file) => {
      const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
      if (!isLtMaxSize) {
        message.error(`File must be smaller than ${maxSize}MB!`);
        return Upload.LIST_IGNORE;
      }

      if (accept !== "*/*") {
        const acceptTypes = accept.split(",").map(type => type.trim());
        const fileType = file.type || "";
        const fileName = file.name || "";
        const isValidType = acceptTypes.some(type => {
          if (type.startsWith(".")) {
            return fileName.toLowerCase().endsWith(type.toLowerCase());
          }
          if (type.includes("/*")) {
            const baseType = type.replace("/*", "/");
            return fileType.startsWith(baseType);
          }
          return fileType === type;
        });

        if (!isValidType) {
          message.error(`File type not allowed. Allowed types: ${accept}`);
          return Upload.LIST_IGNORE;
        }
      }

      // FIXED: Only check maxCount if it's defined
      if (actualMaxCount !== undefined && fileList.length >= actualMaxCount) {
        message.error(`Maximum ${actualMaxCount} file${actualMaxCount > 1 ? 's' : ''} allowed`);
        return Upload.LIST_IGNORE;
      }

      return true;
    };

    const handleChange = ({ fileList: newFileList, file }) => {
      setFileLists(prev => ({
        ...prev,
        [name]: newFileList
      }));

      if (file.status === 'removed') {
        setFileData(prev => {
          const updated = [...(prev[name] || [])];
          const index = updated.findIndex(f => f.uid === file.uid);
          if (index > -1) {
            updated.splice(index, 1);
          }
          return { ...prev, [name]: updated };
        });
      }

      const doneFiles = newFileList
        .filter(f => f.status === 'done')
        .map(f => ({
          name: f.name,
          size: f.size,
          type: f.type,
          lastModified: f.lastModified,
          excelData: f.excelData,
        }));

      const formValue = multiple ? doneFiles : doneFiles[0] || null;

      form.setFieldsValue({ [name]: formValue });
      setFormValues(prev => ({ ...prev, [name]: formValue }));
    };

    const customItemRender = (originNode, file, fileList, actions) => {
      const isExcel = file.name.match(/\.(xls|xlsx)$/i);

      return (
        <div className="ant-upload-list-item" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px',
          marginBottom: '4px',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          backgroundColor: isExcel ? '#f0f9ff' : 'white'
        }}>
          <div className="ant-upload-list-item-info" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {isExcel ? (
              <FileExcelOutlined style={{ marginRight: 8, color: '#1d6f42' }} />
            ) : (
              <PaperClipOutlined style={{ marginRight: 8 }} />
            )}
            <span className="ant-upload-list-item-name" style={{ marginRight: 8, flex: 1 }}>
              {file.name}
              {isExcel && (
                <Tag color="green" style={{ marginLeft: 8, fontSize: '10px' }}>
                  Excel
                </Tag>
              )}
            </span>
            {file.status === 'uploading' && (
              <Progress
                percent={uploadProgress[file.uid] || 0}
                size="small"
                style={{ width: 100 }}
              />
            )}
          </div>
          <div className="ant-upload-list-item-actions">
            {file.status === 'done' && (
              <>
                <Tooltip title="Preview">
                  <EyeOutlined
                    onClick={() => handlePreview(file)}
                    style={{ marginRight: 8, cursor: "pointer" }}
                  />
                </Tooltip>
                {isExcel && excelConfig?.showDetails && (
                  <Tooltip title="Excel Details">
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        Modal.info({
                          title: 'Excel File Details',
                          width: 600,
                          content: (
                            <div>
                              <p><strong>File:</strong> {file.name}</p>
                              <p><strong>Size:</strong> {(file.size / 1024).toFixed(2)} KB</p>
                              <p><strong>Type:</strong> {file.type}</p>
                              {excelConfig.expectedFormat && (
                                <>
                                  <Divider />
                                  <p><strong>Expected Format:</strong></p>
                                  <ul>
                                    {excelConfig.expectedFormat.headers?.map((header, idx) => (
                                      <li key={idx}>{header}</li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </div>
                          ),
                        });
                      }}
                    >
                      Details
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
            <Tooltip title="Remove">
              <DeleteOutlined
                onClick={() => actions.remove()}
                style={{ cursor: "pointer", color: '#ff4d4f' }}
              />
            </Tooltip>
          </div>
        </div>
      );
    };

    const uploadProps = {
      name,
      fileList,
      multiple,
      maxCount: actualMaxCount, // Use the fixed maxCount
      accept,
      listType,
      disabled: isDisabled,
      beforeUpload,
      customRequest: customUpload,
      onChange: handleChange,
      onPreview: handlePreview,
      itemRender: showUploadList ? customItemRender : undefined,
      showUploadList: showUploadList ? {
        showPreviewIcon: true,
        showDownloadIcon: false,
        showRemoveIcon: true,
      } : false,
      action: null,
    };

    if (draggable) {
      return (
        <Dragger {...uploadProps} style={{ marginTop: 8 }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            {maxSize && `Maximum file size: ${maxSize}MB`}
            {accept !== "*/*" && `. Supported formats: ${accept}`}
            <br />
            <small style={{ color: '#999' }}>
              Files are stored locally until you save the form
            </small>
          </p>
        </Dragger>
      );
    }

    return (
      <Upload {...uploadProps} style={{ marginTop: 8 }}>
        <Button icon={<UploadOutlined />} disabled={isDisabled}>
          {label || 'Upload File'}
        </Button>
      </Upload>
    );
  }, [form, isFieldDisabled, handlePreview, fileLists, fileData, uploadProgress]);

  const renderFormItem = useCallback(
    (item) => {
      const disabled = isFieldDisabled(item);
      const fieldType = getDynamicFieldType(item);

      const options = item.optionsCmd
        ? selectOptions[item.name] || []
        : item.options || [];

      const validationState = fieldValidations[item.name];
      const isLoading = validationLoading[item.name];
      const hasValidationButton = item.validationButton;

      const getPrimary = () => {
        if (tenant?.theme?.colorPrimary) return tenant.theme.colorPrimary;
        try {
          const cs = getComputedStyle(document.documentElement);
          const cssVar = (cs.getPropertyValue("--brand-primary") || "").trim();
          if (cssVar) return cssVar;
        } catch { }
        return "#0b5fff";
      };

      const themedDisabledStyle = disabled
        ? {
          backgroundColor: hexToRgba(getPrimary(), 0.06),
          color: "var(--text-primary, rgba(0,0,0,0.85))",
          opacity: 0.92,
          borderColor: "rgba(0,0,0,0.08)",
        }
        : undefined;

      const validationStyle = validationState?.isValid
        ? { borderColor: '#52c41a', boxShadow: '0 0 0 2px rgba(82, 196, 26, 0.1)' }
        : validationState?.isValid === false
          ? { borderColor: '#ff4d4f', boxShadow: '0 0 0 2px rgba(255, 77, 79, 0.1)' }
          : {};

      if (hasValidationButton && (fieldType === 'input' || fieldType === 'number')) {
        return (
          <Input.Group compact key={`${item.name}-${fieldType}`} style={{ display: 'flex' }}>
            {fieldType === 'number' ? (
              <InputNumber
                controls={item.controls === "true"}
                formatter={
                  item.useParser === "true"
                    ? (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    : undefined
                }
                parser={
                  item.useParser === "true"
                    ? (value) => value.replace(/,/g, "")
                    : undefined
                }
                placeholder={item.placeholder}
                disabled={disabled}
                style={{ flex: 1, ...themedDisabledStyle, ...validationStyle }}
                onChange={(value) => {
                  if (validationState) {
                    setFieldValidations(prev => ({ ...prev, [item.name]: null }));
                  }
                }}
                onBlur={(e) => {
                  if (item.validationButton.autoValidateOnBlur && e.target.value) {
                    validateField(item.name, e.target.value);
                  }
                }}
              />
            ) : (
              <Input
                placeholder={item.placeholder}
                disabled={disabled}
                style={{ flex: 1, ...themedDisabledStyle, ...validationStyle }}
                onChange={(e) => {
                  if (validationState) {
                    setFieldValidations(prev => ({ ...prev, [item.name]: null }));
                  }
                }}
                onBlur={(e) => {
                  if (item.validationButton.autoValidateOnBlur && e.target.value) {
                    validateField(item.name, e.target.value);
                  }
                }}
              />
            )}
            <Button
              type="primary"
              loading={isLoading}
              disabled={disabled}
              onClick={() => {
                const value = form.getFieldValue(item.name);
                if (value || value === 0) {
                  validateField(item.name, value);
                } else {
                  message.warning('Please enter a value first');
                }
              }}
              icon={<CheckOutlined />}
              style={{
                backgroundColor: validationState?.isValid ? '#52c41a' : undefined,
                borderColor: validationState?.isValid ? '#52c41a' : undefined
              }}
            >
              {item.validationButton.buttonText || 'Validate'}
            </Button>
          </Input.Group>
        );
      }

      switch (fieldType) {
        case "input":
          return (
            <Input
              key={`${item.name}-input`}
              placeholder={item.placeholder}
              disabled={disabled}
              style={{ ...themedDisabledStyle, ...validationStyle }}
              onChange={(e) => {
                if (item.validationButton?.autoValidate && e.target.value) {
                  validateField(item.name, e.target.value);
                }
                if (validationState) {
                  setFieldValidations(prev => ({ ...prev, [item.name]: null }));
                }
              }}
              onBlur={(e) => {
                if (item.validationButton?.autoValidateOnBlur && e.target.value) {
                  validateField(item.name, e.target.value);
                }
              }}
            />
          );
        case "password":
          return (
            <Input.Password
              key={`${item.name}-password`}
              placeholder={item.placeholder}
              disabled={disabled}
              style={{ ...themedDisabledStyle, ...validationStyle }}
              visibilityToggle={true}
            />
          );
        case "customSelect":
          return (
            <CustomSearchDropdown
              {...item}
              labelInValue={item.entireObject}
              mode={item.mode}
              options={options}
              disabled={disabled}
              style={{ ...themedDisabledStyle, ...validationStyle }}
            />
          );
        case "textarea":
          return (
            <Input.TextArea
              key={`${item.name}-textarea`}
              rows={4}
              placeholder={item.placeholder}
              disabled={disabled}
              style={{ ...themedDisabledStyle, ...validationStyle }}
            />
          );
        case "select":
          return (
            <Select
              key={`${item.name}-select`}
              placeholder={item.placeholder}
              disabled={disabled}
              style={{ ...themedDisabledStyle, ...validationStyle }}
            >
              {options.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          );
        case "switch":
          return (
            <Switch
              key={`${item.name}-switch`}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              disabled={disabled}
              style={themedDisabledStyle}
            />
          );
        case "checkbox":
          return (
            <Checkbox key={`${item.name}-checkbox`} disabled={disabled} style={themedDisabledStyle}>
              {item.text}
            </Checkbox>
          );
        case "date":
          return (
            <DatePicker
              key={`${item.name}-date`}
              style={{ width: "100%", ...validationStyle }}
              disabled={disabled}
            />
          );
        case "dateRange":
          return (
            <RangePicker
              key={`${item.name}-dateRange`}
              style={{ width: "100%", ...validationStyle }}
              disabled={disabled}
            />
          );
        case "number":
          return (
            <InputNumber
              controls={item.controls === "true"}
              formatter={
                item.useParser === "true"
                  ? (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : undefined
              }
              parser={
                item.useParser === "true"
                  ? (value) => value.replace(/,/g, "")
                  : undefined
              }
              key={`${item.name}-number`}
              style={{ width: "100%", ...validationStyle }}
              disabled={disabled}
              onChange={(value) => {
                if (item.validationButton?.autoValidate && value) {
                  validateField(item.name, value);
                }
                if (validationState) {
                  setFieldValidations(prev => ({ ...prev, [item.name]: null }));
                }
              }}
              onBlur={(e) => {
                if (item.validationButton?.autoValidateOnBlur && e.target.value) {
                  validateField(item.name, e.target.value);
                }
              }}
            />
          );
        case "upload":
          return renderUploadField(item);
        default:
          return (
            <Input
              key={`${item.name}-default`}
              disabled={disabled}
              style={{ ...themedDisabledStyle, ...validationStyle }}
            />
          );
      }
    },
    [isFieldDisabled, getDynamicFieldType, selectOptions, renderUploadField, fieldValidations, validationLoading, validateField, form, tenant]
  );

  const renderDescription = useCallback((field) => {
    const {
      items = [],
      layout = "horizontal",
      column = 1,
      bordered = false,
      size = "default",
      labelStyle = {},
      contentStyle = {},
    } = field;

    return (
      <Descriptions
        key={field.key}
        layout={layout}
        column={parseInt(column) ?? 1}
        bordered={bordered}
        size={size}
        style={field.style}
      >
        {items.map((item, idx) => {
          const label = safeEvaluateFunction(item.label, {
            data,
            formValues,
            record: selectedRecord,
            tenant,
          });
          const content = safeEvaluateFunction(item.content, {
            data,
            formValues,
            record: selectedRecord,
            tenant,
          });

          return (
            <Descriptions.Item
              key={item.key || idx}
              label={label}
              span={item.span || 1}
              labelStyle={item.labelStyle || labelStyle}
              contentStyle={item.contentStyle || contentStyle}
            >
              {item.isTag ? (
                <Tag color={item.tagColor}>{content}</Tag>
              ) : item.isLink ? (
                <span
                  className="underline cursor-pointer"
                  onClick={() =>
                    item.onLinkClick &&
                    item.onLinkClick(content, selectedRecord)
                  }
                >
                  {content}
                </span>
              ) : (
                <span
                  style={{
                    color: item.color || "inherit",
                    fontWeight: item.bold ? "bold" : "normal",
                    ...item.style,
                  }}
                >
                  {item.formatCurrency
                    ? formatCurrencyWithoutCode(content)
                    : content}
                </span>
              )}
            </Descriptions.Item>
          );
        })}
      </Descriptions>
    );
  }, [data, formValues, selectedRecord, safeEvaluateFunction, tenant]);

  const hasTableField = useMemo(() => {
    return config?.fields?.some((field) => field.type === "table");
  }, [config?.fields]);

  const hasDataSource = useMemo(() => {
    return !!config?.dataSource;
  }, [config?.dataSource]);

  const shouldShowListView = useMemo(() => {
    return hasTableField || hasDataSource;
  }, [hasTableField, hasDataSource]);

  const getInitialMode = useCallback(() => {
    if (initialMode) return initialMode;
    if (shouldShowListView) {
      return currentRecordId ? "EDIT" : "LIST";
    } else {
      return currentRecordId ? "EDIT" : "NEW";
    }
  }, [initialMode, shouldShowListView, currentRecordId]);

  // Fetch data for list view - SIMPLIFIED
  const fetchData = useCallback(async () => {
    if (!config?.dataSource) return;

    try {
      setLoading(true);
      const { cmd, params = {} } = config.dataSource;
      const requestParams = { ...params, ...pagination, ...extraParams };
      const response = await postAuthenticatedRequest(cmd, requestParams);
      const result = response?.outData;

      if (result?.isSuccessful) {
        setData(result || []);
        // Don't show message for list view fetches to reduce noise
      } else {
        console.error("Failed to fetch data:", result?.message);
        message.error(result?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [config?.dataSource, pagination, extraParams]);

  // Reset form with proper default values including review state
  const resetFormWithDefaults = useCallback(() => {
    const defaultValues = {};
    if (config?.formFields) {
      const formDefaults = generateInitialFormValues(config.formFields);
      Object.assign(defaultValues, formDefaults);
    }

    const formField = config?.fields?.find((field) => field.type === "form");
    if (formField?.items) {
      const formItemDefaults = generateInitialFormValues(formField.items);
      Object.assign(defaultValues, formItemDefaults);
    }

    defaultValues.IsSensitive = false;

    form.setFieldsValue(defaultValues);
    setFormValues(defaultValues);
    setShowReview(false);
    setCurrentStep(0);
    setReviewData({});
    setSelectedRecord(null);
    setCurrentRecordId(null);
    setFileLists({});
    setFileData({});
    setFieldValidations({});
    setValidationLoading({});
  }, [config, form, generateInitialFormValues]);

  // Map initial data from external source using dataMapper
  const mapInitialData = useCallback(
    (recordData) => {
      if (!recordData || !config?.formFields) return recordData;

      const mappedData = { ...recordData };

      config.formFields.forEach((field) => {
        if (field.dataMapper && recordData) {
          const value = field.dataMapper
            .split(".")
            .reduce(
              (obj, key) => (obj && obj[key] !== undefined ? obj[key] : null),
              recordData
            );

          if (value !== null && value !== undefined) {
            mappedData[field.name] = value;
          }
        }
      });

      return mappedData;
    },
    [config?.formFields]
  );

  // Load record - OPTIMIZED to prevent multiple calls
  const loadRecord = useCallback(async (id) => {
    if (!id) return;

    // Prevent multiple calls for the same record
    if (currentRecordId === id && selectedRecord) {
      console.log("Record already loaded, skipping");
      return;
    }

    // If we already have the record data from props, use it
    if (record && record.id === id) {
      const mappedData = mapInitialData(record);
      setSelectedRecord(mappedData);
      setCurrentRecordId(id);
      form.setFieldsValue(mappedData);
      setFormValues(mappedData);
      return;
    }

    // Otherwise fetch from API
    if (!config?.operations?.view?.cmd) return;

    try {
      setLoading(true);
      const response = await postAuthenticatedRequest(
        config.operations.view.cmd,
        { id, ...extraParams }
      );
      const result = response?.outData;

      if (result?.isSuccessful) {
        const recordData = { id, ...result.data };
        const mappedData = mapInitialData(recordData);

        setSelectedRecord(mappedData);
        setCurrentRecordId(id);
        form.setFieldsValue(mappedData);
        setFormValues(mappedData);
      } else {
        message.error(result?.message || "Failed to load record");
      }
    } catch (error) {
      console.error("Error loading record:", error);
      message.error("Error loading record");
    } finally {
      setLoading(false);
    }
  }, [config?.operations?.view?.cmd, form, record, extraParams, mapInitialData, currentRecordId, selectedRecord]);

  // Handle CRUD operations - FIXED: Review flow
  const handleCrudOperation = useCallback(
    async (operation, record = null, formData = null) => {
      // If it's a save operation and onSaveMethod is provided, call it
      if ((operation === "create" || operation === "update") && onSaveMethod) {
        try {
          setLoading(true);

          const processedData = { ...formData };
          const filesToUpload = {};

          Object.entries(fileData).forEach(([fieldName, files]) => {
            if (files && files.length > 0) {
              filesToUpload[fieldName] = files;
            }
          });

          const saveData = {
            operation,
            formData: processedData,
            files: filesToUpload,
            recordId: record?.id || selectedRecord?.id || currentRecordId,
            isUpdate: operation === "update",
            isCreate: operation === "create",
            record: selectedRecord,
            currentRecordId,
            extraParams,
          };

          const result = await onSaveMethod(saveData);

          if (result?.isSuccessful !== false) {
            message.success(result?.message || "Operation completed successfully");

            if (operation === "create") {
              const createdRecord = result?.data;

              if (shouldShowListView) {
                setPageMode("LIST");
                fetchData();
              } else {
                resetFormWithDefaults();
                setPageMode("NEW");
              }

              setCurrentRecordId(null);
              setSelectedRecord(null);
            } else if (operation === "update") {
              if (shouldShowListView) {
                fetchData();
                setPageMode("LIST");
              } else {
                if (currentRecordId) {
                  loadRecord(currentRecordId);
                }
              }
            }

            if (onModeChange) {
              const newMode =
                operation === "update"
                  ? shouldShowListView
                    ? "LIST"
                    : "EDIT"
                  : operation === "create"
                    ? shouldShowListView
                      ? "LIST"
                      : "NEW"
                    : pageMode;
              onModeChange(newMode);
            }

            if (onRefresh && operation != "view") {
              onRefresh();
            }

            // FIXED: Reset steps after successful submission
            if (isReviewEnabled && (operation === "create" || operation === "update")) {
              setCurrentStep(0); // Go back to form step
              setShowReview(false);
            }

            return;
          } else {
            message.error(result?.message || "Operation failed");
          }
        } catch (error) {
          console.error(`Error during ${operation}:`, error);
          message.error(`Error during ${operation}`);
        } finally {
          setLoading(false);
        }

        return;
      }

      // Original logic for when onSaveMethod is not provided
      try {
        setLoading(true);
        let cmd, params;

        switch (operation) {
          case "create":
            cmd = config.operations?.create?.cmd || "CreateCommand";
            params = formData;
            break;
          case "update":
            cmd = config.operations?.update?.cmd || "UpdateCommand";
            params = {
              ...formData,
              id: record?.id || selectedRecord?.id || currentRecordId,
            };
            break;
          case "delete":
            cmd = config.operations?.delete?.cmd || "DeleteCommand";
            params = { id: record?.id };
            break;
          case "view":
            cmd = config.operations?.view?.cmd || "GetByIdQuery";
            params = { id: record?.id };
            break;
          default:
            return;
        }

        const mergedParams = { ...params, ...extraParams };
        const response = await postAuthenticatedRequest(cmd, mergedParams);
        const result = response?.outData;

        if (result?.isSuccessful) {
          message.success(result?.message || `${operation} successful`);

          if (operation === "view") {
            setSelectedRecord(record);
            setCurrentRecordId(record?.id);
            setPageMode("EDIT");
            form.setFieldsValue(result.data);
            setFormValues(result.data);
          } else if (operation === "create") {
            const createdRecord = result.data;

            if (shouldShowListView) {
              setPageMode("LIST");
              fetchData();
            } else {
              resetFormWithDefaults();
              setPageMode("NEW");
            }

            setCurrentRecordId(null);
            setSelectedRecord(null);
          } else if (operation === "update") {
            if (shouldShowListView) {
              fetchData();
              setPageMode("LIST");
            } else {
              if (currentRecordId) {
                loadRecord(currentRecordId);
              }
            }
          } else if (operation === "delete") {
            if (shouldShowListView) {
              fetchData();
              setPageMode("LIST");
            } else {
              resetFormWithDefaults();
              setPageMode("NEW");
            }
          }

          if (onModeChange) {
            const newMode =
              operation === "view"
                ? "EDIT"
                : operation === "create"
                  ? shouldShowListView
                    ? "LIST"
                    : "NEW"
                  : operation === "update"
                    ? shouldShowListView
                      ? "LIST"
                      : "EDIT"
                    : operation === "delete"
                      ? shouldShowListView
                        ? "LIST"
                        : "NEW"
                      : pageMode;
            onModeChange(newMode);
          }

          if (onRefresh && operation != "view") {
            onRefresh();
          }

          // FIXED: Reset steps after successful submission
          if (isReviewEnabled && (operation === "create" || operation === "update")) {
            setCurrentStep(0); // Go back to form step
            setShowReview(false);
          }
        } else {
          message.error(
            result?.message || response?.message || `${operation} failed`
          );
        }
      } catch (error) {
        console.error(`Error during ${operation}:`, error);
        message.error(`Error during ${operation}`);
      } finally {
        setLoading(false);
      }
    },
    [
      config,
      form,
      fetchData,
      selectedRecord,
      currentRecordId,
      shouldShowListView,
      onModeChange,
      resetFormWithDefaults,
      isReviewEnabled,
      extraParams,
      onSaveMethod,
      onRefresh,
      fileData,
      loadRecord,
    ]
  );

  const handleFormSubmit = useCallback(
    async (values) => {
      const operation = currentRecordId ? "update" : "create";
      await handleCrudOperation(operation, selectedRecord, values);
    },
    [selectedRecord, handleCrudOperation, currentRecordId]
  );

  // Handle review screen - FIXED: Should move to review step, not save
  const handleReview = useCallback(
    async (values) => {
      if (isReviewEnabled) {
        setReviewData(values);
        setShowReview(true);
        setCurrentStep(1); // Move to review step (index 1)
      } else {
        // If no review screen, proceed directly to save
        await handleFormSubmit(values);
      }
    },
    [isReviewEnabled, handleFormSubmit]
  );

  // Handle final submission after review
  const handleFinalSubmit = useCallback(async () => {
    await handleFormSubmit(reviewData);
  }, [reviewData, handleFormSubmit]);

  // Cancel review and go back to form
  const handleCancelReview = useCallback(() => {
    setShowReview(false);
    setCurrentStep(0); // Go back to form step
  }, []);

  // ========== FIXED: SIMPLIFIED USE EFFECTS ==========

  // Effect 1: Mount/Unmount tracking
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      initializedRef.current = false;
    };
  }, []);

  // Effect 2: Reset everything when drawer closes
  useEffect(() => {
    if (!visible && mountedRef.current) {
      console.log("Drawer closing, resetting state");
      initializedRef.current = false;
      configRef.current = null;
      recordIdRef.current = null;
      recordRef.current = null;

      setSelectedRecord(null);
      setCurrentRecordId(null);
      setFormValues({});
      setShowReview(false);
      setCurrentStep(0);
      setReviewData({});
      setPageMode("LIST");
      setActiveTab("main");
      setUploadProgress({});
      setPreviewVisible(false);
      setFileLists({});
      setFileData({});
      setFieldValidations({});
      setValidationLoading({});
      form.resetFields();
      setData({});
      setConfig(null);
      setSelectOptions({});
    }
  }, [visible, form]);

  // Effect 3: Load config only when drawer opens
  useEffect(() => {
    const loadConfig = async () => {
      if (!visible || !configCmd || initializedRef.current) return;

      try {
        setConfigLoading(true);
        const response = await postAuthenticatedRequest(configCmd, configParams);
        const result = response?.outData;

        if (result?.isSuccessful) {
          setConfig(result.data);
          configRef.current = result.data;

          if (result.data?.fields) {
            const tabsField = result.data.fields.find((f) => f.type === "tabs");
            if (tabsField?.tabs?.[0]) {
              setActiveTab(tabsField.tabs[0].key);
            }
          }
        } else {
          message.error(result?.message || "Failed to load drawer configuration");
        }
      } catch (error) {
        console.error("Error fetching drawer config:", error);
        message.error("Error loading drawer configuration");
      } finally {
        setConfigLoading(false);
      }
    };

    if (visible && !initializedRef.current) {
      console.log("Loading config for drawer");
      loadConfig();
    }
  }, [visible, configCmd, configParams]);

  // Effect 4: Initialize after config loads
  useEffect(() => {
    if (visible && config && !configLoading && !initializedRef.current) {
      console.log("Config loaded, initializing drawer");

      // Determine initial mode
      const initialRecordId = recordId || (record ? record.id : null);
      const newMode = initialMode || (initialRecordId ? "EDIT" : shouldShowListView ? "LIST" : "NEW");

      console.log("Initial recordId:", initialRecordId, "Mode:", newMode);

      setPageMode(newMode);
      setCurrentRecordId(initialRecordId);

      // Load record if we have an ID
      if (initialRecordId && (newMode === "EDIT" || newMode === "VIEW")) {
        console.log("Loading initial record:", initialRecordId);
        loadRecord(initialRecordId);
      } else if (newMode === "NEW") {
        console.log("Initializing new form");
        resetFormWithDefaults();
      }

      // Load select options
      if (config.formFields) {
        loadAllSelectOptions(config.formFields);
      }

      // Fetch data for list view
      if (shouldShowListView && newMode === "LIST") {
        console.log("Fetching initial list data");
        fetchData();
      }

      initializedRef.current = true;
      recordIdRef.current = recordId;
      recordRef.current = record;
    }
  }, [visible, config, configLoading, recordId, record, initialMode, shouldShowListView, loadRecord, resetFormWithDefaults, loadAllSelectOptions, fetchData]);

  // Effect 5: Handle recordId changes when drawer is already open
  useEffect(() => {
    if (visible && config && initializedRef.current && recordId !== recordIdRef.current) {
      console.log("RecordId changed from", recordIdRef.current, "to", recordId);

      recordIdRef.current = recordId;
      setCurrentRecordId(recordId);

      if (recordId && (pageMode === "EDIT" || pageMode === "VIEW")) {
        console.log("Loading updated record:", recordId);
        loadRecord(recordId);
      } else if (!recordId && pageMode === "NEW") {
        console.log("Resetting to new form");
        resetFormWithDefaults();
      }
    }
  }, [visible, config, recordId, pageMode, loadRecord, resetFormWithDefaults]);

  // Effect 6: Handle record prop changes
  useEffect(() => {
    if (visible && config && initializedRef.current && record !== recordRef.current) {
      console.log("Record prop changed");

      recordRef.current = record;
      setSelectedRecord(record);

      if (record?.id) {
        const recordId = record.id;
        setCurrentRecordId(recordId);
        const mappedData = mapInitialData(record);
        form.setFieldsValue(mappedData);
        setFormValues(mappedData);
      }
    }
  }, [visible, config, record, form, mapInitialData]);

  // Effect 7: Initialize file lists from form values
  useEffect(() => {
    if (config?.formFields && formValues && Object.keys(formValues).length > 0) {
      const newFileLists = {};
      const newFileData = {};

      config.formFields.forEach(field => {
        if (field.fieldType === 'upload' && formValues[field.name]) {
          const value = formValues[field.name];
          const initialFiles = Array.isArray(value) ? value : [value].filter(Boolean);

          newFileLists[field.name] = initialFiles.map((file, index) => {
            if (file.uid) {
              return file;
            }

            return {
              uid: `initial-${Date.now()}-${index}`,
              name: file.name || file.fileName || `File ${index + 1}`,
              status: 'done',
              url: file.url || file.path || file.response?.url,
              size: file.size,
              type: file.type,
              response: file,
            };
          });

          newFileData[field.name] = initialFiles;
        }
      });

      if (Object.keys(newFileLists).length > 0) {
        setFileLists(prev => ({ ...prev, ...newFileLists }));
      }
      if (Object.keys(newFileData).length > 0) {
        setFileData(prev => ({ ...prev, ...newFileData }));
      }
    }
  }, [config?.formFields, formValues]);

  // ========== END OF USE EFFECTS ==========

  const handlePaginationChange = useCallback((page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: page - 1,
      pageSize: pageSize,
    }));

    // Fetch new page data
    if (config?.dataSource && shouldShowListView && pageMode === "LIST") {
      fetchData();
    }
  }, [config?.dataSource, shouldShowListView, pageMode, fetchData]);

  const handleActionClick = useCallback(
    (record, actionKey) => {
      const action = config.fields
        ?.flatMap((field) => field.actionOptions || [])
        .find((opt) => opt.key === actionKey);

      switch (actionKey) {
        case "view":
        case "edit":
          handleCrudOperation("view", record);
          break;
        case "delete":
          Modal.confirm({
            title: "Confirm Delete",
            content: `Are you sure you want to delete this item?`,
            okText: "Delete",
            okType: "danger",
            onOk: () => handleCrudOperation("delete", record),
          });
          break;
        default:
          if (action?.onClick) {
            if (typeof action.onClick === "function") {
              action.onClick(record, data);
            } else if (typeof action.onClick === "string") {
              const func = new Function("record", "data", action.onClick);
              func(record, data);
            }
          }
          break;
      }
    },
    [config, data, handleCrudOperation]
  );

  const handleExtraButtonClick = useCallback(
    (buttonKey) => {
      const newMode = (() => {
        switch (buttonKey) {
          case "new":
            setSelectedRecord(null);
            setCurrentRecordId(null);
            resetFormWithDefaults();
            return "NEW";
          case "back":
            setSelectedRecord(null);
            setCurrentRecordId(null);
            resetFormWithDefaults();
            return shouldShowListView ? "LIST" : "NEW";
          case "save":
            if (showReview) {
              // If in review, submit the form
              handleFinalSubmit();
            } else {
              // If not in review, submit the form to go to review or save directly
              form.submit();
            }
            return pageMode;
          case "refresh":
            if (shouldShowListView && pageMode === "LIST") {
              fetchData();
            } else if (currentRecordId) {
              loadRecord(currentRecordId);
            }
            return pageMode;
          case "export":
            console.log("Export clicked");
            return pageMode;
          default:
            console.log("Extra button clicked:", buttonKey);
            return pageMode;
        }
      })();

      if (newMode !== pageMode) {
        setPageMode(newMode);
        if (onModeChange) {
          onModeChange(newMode);
        }
      }
    },
    [
      form,
      fetchData,
      shouldShowListView,
      pageMode,
      currentRecordId,
      onModeChange,
      resetFormWithDefaults,
      loadRecord,
      showReview,
      handleFinalSubmit,
    ]
  );

  const getProcessedColumns = useCallback(
    (columns, actionOptions) => {
      if (!actionOptions) return columns;

      return [
        ...columns.map((col) => ({
          ...col,
          render: col.render
            ? (text, record, index) =>
              safeEvaluateFunction(col.render, { data, record, index, tenant })
            : undefined,
        })),
        {
          title: "",
          key: "action",
          fixed: "right",
          width: 80,
          render: (_, record) => (
            <Dropdown
              menu={{
                items: actionOptions.map((opt) => ({
                  key: opt.key,
                  label: opt.label,
                  icon: getIconComponent(opt.icon),
                  danger: opt.danger,
                  disabled:
                    typeof opt.disabled === "function"
                      ? safeEvaluateFunction(opt.disabled, { record, data, tenant })
                      : opt.disabled,
                })),
                onClick: ({ key }) => handleActionClick(record, key),
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <MoreOutlined style={{ cursor: "pointer" }} />
            </Dropdown>
          ),
        },
      ];
    },
    [data, safeEvaluateFunction, handleActionClick, tenant]
  );

  // Render review screen
  const renderReviewScreen = useCallback(() => {
    if (!showReview) return null;

    const reviewFields = config?.reviewFields || config?.formFields || [];

    return (
      <div className="review-screen">
        <Alert
          message="Please review your information before submitting"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Descriptions
          bordered
          column={1}
          size="small"
          style={{ marginBottom: 16 }}
        >
          {reviewFields.map((field) => {
            if (
              field.hidden &&
              safeEvaluateFunction(field.hidden, {
                formValues: reviewData,
                record: selectedRecord,
                tenant
              })
            ) {
              return null;
            }

            const value = reviewData[field.name];
            let displayValue = value;

            if (
              field.fieldType === "switch" ||
              field.fieldType === "checkbox"
            ) {
              displayValue = value ? "Yes" : "No";
            } else if (field.fieldType === "date" && value) {
              displayValue = new Date(value).toLocaleDateString();
            } else if (field.fieldType === "select" && field.options) {
              const option = field.options.find((opt) => opt.value === value);
              displayValue = option ? option.label : value;
            } else if (
              field.fieldType === "customSelect" &&
              selectOptions[field.name]
            ) {
              const option = selectOptions[field.name].find(
                (opt) => opt.value === value
              );
              displayValue = option ? option.label : value;
            } else if (field.fieldType === "upload") {
              if (Array.isArray(value)) {
                displayValue = (
                  <div>
                    {value.map((f, idx) => (
                      <div key={idx} style={{ marginBottom: 4 }}>
                        <Tag color="blue">
                          <PaperClipOutlined style={{ marginRight: 4 }} />
                          {f.name} ({(f.size / 1024).toFixed(1)} KB)
                        </Tag>
                        {f.excelData && (
                          <Tag color="green" style={{ marginLeft: 4 }}>
                            Excel
                          </Tag>
                        )}
                      </div>
                    ))}
                  </div>
                );
              } else if (value) {
                displayValue = (
                  <Tag color="blue">
                    <PaperClipOutlined style={{ marginRight: 4 }} />
                    {value.name} ({(value.size / 1024).toFixed(1)} KB)
                    {value.excelData && (
                      <Tag color="green" style={{ marginLeft: 4 }}>
                        Excel
                      </Tag>
                    )}
                  </Tag>
                );
              } else {
                displayValue = "No file uploaded";
              }
            } else if (Array.isArray(value)) {
              displayValue = value.join(", ");
            }

            return (
              <Descriptions.Item key={field.name} label={field.label}>
                {displayValue || "-"}
              </Descriptions.Item>
            );
          })}
        </Descriptions>

        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={handleCancelReview} icon={<EditOutlined />}>
            Edit
          </Button>
          <Button
            type="primary"
            onClick={handleFinalSubmit}
            loading={loading}
            icon={<SaveOutlined />}
          >
            Confirm & Submit
          </Button>
        </Space>
      </div>
    );
  }, [
    showReview,
    config,
    reviewData,
    selectOptions,
    loading,
    handleCancelReview,
    handleFinalSubmit,
    safeEvaluateFunction,
    selectedRecord,
    tenant,
  ]);

  // Render step indicator - FIXED: Only show form and review steps
  const renderStepIndicator = useCallback(() => {
    if (!isReviewEnabled || pageMode === "LIST") return null;

    return (
      <Steps current={currentStep} size="small" style={{ marginBottom: 24 }}>
        {steps.map((step) => (
          <Step key={step.key} title={step.title} />
        ))}
      </Steps>
    );
  }, [isReviewEnabled, pageMode, currentStep, steps]);

  const renderForm = useCallback(
    (field) => {
      const {
        items = [],
        layout = "vertical",
        initialValues = {},
        formLayout = "vertical",
        columns = 1,
        responsive = true,
      } = field;

      const formDefaults = generateInitialFormValues(items);
      const combinedInitialValues = {
        ...formDefaults,
        ...initialValues,
        IsSensitive: false,
      };

      const getGridClasses = () => {
        if (!responsive) return "";

        switch (columns) {
          case 1:
            return "grid grid-cols-1 gap-4";
          case 2:
            return "grid grid-cols-1 md:grid-cols-2 gap-4";
          case 3:
            return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";
          case 4:
            return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4";
          default:
            return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";
        }
      };

      return (
        <Form
          form={form}
          layout={formLayout}
          initialValues={combinedInitialValues}
          onFinish={handleReview} // This goes to review or save
          onValuesChange={handleFormValuesChange}
        >
          <div className={getGridClasses()}>
            {items.map((item) => {
              if (
                item.hidden &&
                safeEvaluateFunction(item.hidden, {
                  formValues,
                  selectedRecord,
                  record: selectedRecord,
                  tenant,
                })
              ) {
                return null;
              }

              return (
                <Form.Item
                  key={item.name}
                  label={item.label}
                  name={item.name}
                  tooltip={item.helpText}
                  rules={item.rules}
                  className="mb-4"
                  initialValue={getDefaultValueForFieldType(item)}
                  extra={item.help && (
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      {item.help}
                    </div>
                  )}
                >
                  {renderFormItem(item)}
                </Form.Item>
              );
            })}
          </div>
        </Form>
      );
    },
    [
      form,
      handleReview,
      handleFormValuesChange,
      renderFormItem,
      formValues,
      safeEvaluateFunction,
      generateInitialFormValues,
      getDefaultValueForFieldType,
      selectedRecord,
      tenant,
    ]
  );

  const renderTabs = useCallback((field) => {
    const { tabs = [], tabPosition = "top" } = field;

    return (
      <Tabs
        key={field.key}
        activeKey={activeTab}
        onChange={setActiveTab}
        tabPosition={tabPosition}
      >
        {tabs.map((tab) => (
          <TabPane tab={tab.label} key={tab.key}>
            {renderField(tab.content)}
          </TabPane>
        ))}
      </Tabs>
    );
  }, [activeTab]);

  const renderText = useCallback((field) => {
    const { content, level = 1, type, strong, italic } = field;
    let textContent = safeEvaluateFunction(content, { data, tenant });

    return (
      <Text
        key={field.key}
        type={type}
        strong={strong}
        italic={italic}
        style={field.style}
      >
        {textContent}
      </Text>
    );
  }, [data, safeEvaluateFunction, tenant]);

  const renderAlert = useCallback((field) => {
    const {
      message: alertMessage,
      description,
      alertType = "info",
      showIcon,
    } = field;
    const processedMessage = safeEvaluateFunction(alertMessage, { data, tenant });
    const processedDescription = safeEvaluateFunction(description, { data, tenant });

    return (
      <Alert
        key={field.key}
        message={processedMessage}
        description={processedDescription}
        type={alertType}
        showIcon={showIcon}
        style={field.style}
      />
    );
  }, [data, safeEvaluateFunction, tenant]);

  const renderViewAlert = useCallback((field) => {
    if (pageMode === "LIST" || pageMode === "NEW") {
      return null;
    }

    const {
      template,
      configMessage,
      responseFields = [],
      configFields = {},
      separator = " • ",
      alertType = "info",
      showIcon = true,
      message: fallbackMessage,
      description: fallbackDescription,
    } = field;

    let alertMessage = "";
    let alertDescription = "";

    try {
      if (template) {
        alertMessage = template.replace(/\${([^}]+)}/g, (match, path) => {
          const value = getNestedValue(
            { record: selectedRecord, formValues, data, tenant },
            path
          );
          return value !== null && value !== undefined ? String(value) : "";
        });
      } else if (
        configMessage ||
        responseFields.length > 0 ||
        Object.keys(configFields).length > 0
      ) {
        const parts = [];

        if (configMessage) {
          parts.push(configMessage);
        }

        responseFields.forEach((fieldPath) => {
          const value = getNestedValue(selectedRecord || formValues, fieldPath);
          if (value !== null && value !== undefined && value !== "") {
            parts.push(String(value));
          }
        });

        Object.entries(configFields).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== "") {
            parts.push(String(value));
          }
        });

        alertMessage = parts.join(separator);
      } else {
        alertMessage = fallbackMessage || "";
        alertDescription = fallbackDescription || "";
      }
    } catch (error) {
      console.error("Error building viewAlert content:", error);
      alertMessage = fallbackMessage || "Error building alert content";
      alertDescription = fallbackDescription || "";
    }

    if (!alertMessage && !alertDescription) {
      return null;
    }

    return (
      <Alert
        key={field.key}
        message={alertMessage}
        description={alertDescription}
        type={alertType}
        showIcon={showIcon}
        style={field.style}
      />
    );
  }, [pageMode, selectedRecord, formValues, data, tenant]);

  const renderButtons = useCallback((field) => {
    const { buttons = [], direction = "horizontal" } = field;

    return (
      <Space key={field.key} direction={direction} style={{ width: "100%" }}>
        {buttons.map((button) => (
          <Button
            key={button.key}
            type={button.type}
            icon={getIconComponent(button.icon)}
            danger={button.danger}
            loading={button.loading}
            onClick={() => handleExtraButtonClick(button.key)}
          >
            {button.label}
          </Button>
        ))}
      </Space>
    );
  }, [handleExtraButtonClick]);

  const renderCard = useCallback((field) => {
    const { title, content, extra, hoverable = true } = field;
    const cardTitle = safeEvaluateFunction(title, { data, tenant });

    return (
      <Card
        key={field.key}
        title={cardTitle}
        extra={extra}
        hoverable={hoverable}
        style={field.style}
      >
        {renderField(content)}
      </Card>
    );
  }, [data, safeEvaluateFunction, tenant]);

  const renderTable = useCallback((field) => {
    const {
      columns = [],
      rowKey = "id",
      pagination: tablePagination = true,
      scroll = { x: "max-content" },
      actionOptions,
      tableDataPath,
    } = field;

    const tableData = tableDataPath
      ? getNestedValue(data, tableDataPath)
      : data;
    const processedColumns = getProcessedColumns(columns, actionOptions);

    return (
      <Table
        key={field.key}
        columns={processedColumns}
        dataSource={Array.isArray(tableData) ? tableData : []}
        rowKey={rowKey}
        loading={loading}
        scroll={scroll}
        pagination={
          tablePagination && {
            current: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            total: data.totalRows || 0,
            pageSizeOptions: ["10", "20", "50", "100"],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total}`,
            onChange: handlePaginationChange,
          }
        }
      />
    );
  }, [data, loading, pagination, getProcessedColumns, handlePaginationChange]);

  const renderField = useCallback((field) => {
    if (!field) return null;

    switch (field.type) {
      case "table":
        return renderTable(field);
      case "viewAlert":
        return renderViewAlert(field);
      case "form":
        return renderForm(field);
      case "buttons":
        return renderButtons(field);
      case "tabs":
        return renderTabs(field);
      case "text":
        return renderText(field);
      case "alert":
        return renderAlert(field);
      case "card":
        return renderCard(field);
      case "divider":
        return <Divider key={field.key} />;
      case "description":
        return renderDescription(field);
      case "custom":
        return field.render
          ? safeEvaluateFunction(field.render, { data, loading, refresh: fetchData, tenant })
          : null;
      default:
        return <Empty description={`Unsupported field type: ${field.type}`} />;
    }
  }, [
    renderTable,
    renderViewAlert,
    renderForm,
    renderButtons,
    renderTabs,
    renderText,
    renderAlert,
    renderCard,
    renderDescription,
    safeEvaluateFunction,
    data,
    loading,
    fetchData,
    tenant,
  ]);

  // Render form fields
  const renderFormFields = useCallback(() => {
    if (!config?.formFields) return null;

    return renderField({
      type: "form",
      items: config.formFields,
      layout: "vertical",
      columns: formFieldsColumn ?? 2,
      responsive: true,
    });
  }, [config?.formFields, renderField, formFieldsColumn]);

  // Render form content based on whether review is shown
  const renderFormContent = useCallback(() => {
    if (showReview) {
      return renderReviewScreen();
    }

    return (
      <div>
        {config?.fields
          ?.filter((field) => field.type !== "table")
          .map((field, index) => (
            <div key={field.key || index} style={{ marginBottom: 16 }}>
              {field.title && (
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{field.title}</Text>
                  {field.description && (
                    <Text
                      type="secondary"
                      style={{ display: "block", fontSize: 12 }}
                    >
                      {field.description}
                    </Text>
                  )}
                </div>
              )}
              {renderField(field)}
              {field.divider && <Divider />}
            </div>
          ))}

        {renderFormFields()}

        {!config?.formFields &&
          config?.fields?.find((f) => f.type === "form") &&
          renderField(config.fields.find((f) => f.type === "form"))}
      </div>
    );
  }, [showReview, config, renderReviewScreen, renderField, renderFormFields]);

  const getCurrentTitle = useCallback(() => {
    if (pageMode === "LIST") return config?.title;
    if (pageMode === "NEW") return `New ${config?.title}`;
    if (pageMode === "EDIT" || pageMode === "VIEW")
      return `Edit ${config?.title}`;
    return config?.title;
  }, [pageMode, config?.title]);

  const getExtraButtons = useCallback(() => {
    const buttons = [];

    if (pageMode === "LIST") {
      if (config?.operations?.create || onSaveMethod) {
        buttons.push({
          key: "new",
          label: "New",
          icon: <PlusOutlined />,
        });
      }
      if (shouldShowListView) {
        buttons.push({
          key: "refresh",
          label: "Refresh",
          icon: <ReloadOutlined />,
        });
      }
    } else {
      if (shouldShowListView) {
        buttons.push({
          key: "back",
          label: "Back",
          icon: <ArrowLeftOutlined />,
        });
      }

      // FIXED: Save button should show "Next" when review is enabled and not in review
      if (config?.operations?.create || config?.operations?.update || onSaveMethod) {
        const saveLabel = isReviewEnabled && !showReview ? "Next" : (currentRecordId ? "Update" : "Save");
        buttons.push({
          key: "save",
          label: saveLabel,
          icon: isReviewEnabled && !showReview ? <ArrowLeftOutlined /> : <SaveOutlined />,
          type: "primary",
        });
      }
    }

    return buttons;
  }, [pageMode, config, onSaveMethod, shouldShowListView, currentRecordId, isReviewEnabled, showReview]);

  if (configLoading) {
    return (
      <Drawer
        title="Loading..."
        width={width}
        placement={placement}
        closable={closable}
        onClose={onClose}
        open={visible}
        destroyOnClose={destroyOnClose}
      >
        <Skeleton active paragraph={{ rows: 6 }} />
      </Drawer>
    );
  }

  if (!config) {
    return (
      <Drawer
        title="Configuration Error"
        width={width}
        placement={placement}
        closable={closable}
        onClose={onClose}
        open={visible}
        destroyOnClose={destroyOnClose}
      >
        <Alert
          message="Unable to load configuration"
          description="Please check your configuration endpoint"
          type="error"
          showIcon
        />
      </Drawer>
    );
  }

  const extraButtons = getExtraButtons();
  const currentTitle = getCurrentTitle();

  return (
    <>
      <Drawer
        title={
          <Space direction="vertical" size={0}>
            <Title level={4} style={{ marginBottom: 0 }}>
              {currentTitle}
            </Title>
            {config.subtitle && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {config.subtitle}
              </Text>
            )}
          </Space>
        }
        width={width}
        placement={placement}
        closable={closable}
        onClose={onClose}
        open={visible}
        destroyOnClose={destroyOnClose}
        zIndex={config?.zIndex || 200}
        extra={
          <Space>
            {extraButtons.map((button) => (
              <Button
                key={button.key}
                type={button.type}
                icon={button.icon}
                onClick={() => handleExtraButtonClick(button.key)}
              >
                {button.label}
              </Button>
            ))}
          </Space>
        }
      >
        <Spin spinning={loading}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {renderStepIndicator()}

            {pageMode === "LIST" && shouldShowListView
              ? config.fields?.map((field, index) => (
                <div key={field.key || index}>
                  {field.title && (
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>{field.title}</Text>
                      {field.description && (
                        <Text
                          type="secondary"
                          style={{ display: "block", fontSize: 12 }}
                        >
                          {field.description}
                        </Text>
                      )}
                    </div>
                  )}
                  {renderField(field)}
                  {field.divider && <Divider />}
                </div>
              ))
              : renderFormContent()}
          </Space>
        </Spin>
      </Drawer>

      {/* Image preview modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
      >
        {previewImage && (
          <img
            alt="Preview"
            style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain' }}
            src={previewImage}
          />
        )}
      </Modal>
    </>
  );
};

export default DynamicDrawer;